<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppScreen from '@/components/layout/AppScreen.vue'
import MapControls from '@/components/map/MapControls.vue'
import type { MapMode } from '@/components/map/mapMode'
import MapView from '@/components/map/MapView.vue'
import { useMapControls } from '@/composables/useMapControls'
import { useRouteVehiclesQuery } from '@/composables/useRouteVehiclesQuery'
import { useTransitIndex } from '@/composables/useTransitIndex'
import type { MapVehicle } from '@/types'
import { resolveVehicleSnapshot } from '@/utils/vehicleSnapshot'

const props = defineProps<{ routeCode: string }>()

const route = useRoute()
const router = useRouter()
const { index } = useTransitIndex()
const {
  basemap,
  toggleBasemap,
  location: userLocation,
  isLocating,
  error: locationError,
  locate,
} = useMapControls()

const busRoute = computed(() => index.value?.routesByCode.get(props.routeCode))

/** Optional back-context: which stop the user opened this route from. */
const originStop = computed(() => {
  const raw = route.query.stop
  const id = Number(Array.isArray(raw) ? raw[0] : raw)
  return Number.isSafeInteger(id) && id > 0 ? index.value?.stopsById.get(id) : undefined
})

const query = useRouteVehiclesQuery(busRoute)

const snapshot = computed(() =>
  resolveVehicleSnapshot({
    vehicles: query.data.value ?? [],
    isPending: query.isPending.value,
    hasError: Boolean(query.error.value),
  }),
)

const vehicles = computed<MapVehicle[]>(() => snapshot.value.vehicles)

const mode = computed<MapMode>(() =>
  busRoute.value
    ? { kind: 'route', route: busRoute.value, vehicles: vehicles.value }
    : { kind: 'stops', stops: [] },
)

const status = computed(() => {
  if (snapshot.value.source === 'checking') return 'Connecting…'
  if (snapshot.value.source === 'last-known') return 'Connection lost — last known positions'
  if (query.isError.value) return 'Live bus data is unavailable'
  if (vehicles.value.length === 0) return 'No buses currently running'
  return `${vehicles.value.length} bus${vehicles.value.length === 1 ? '' : 'es'} running`
})

// An unknown route code is a dead end; send the user somewhere usable.
watch(
  [index, busRoute],
  ([loadedIndex, found]) => {
    if (loadedIndex && !found) router.replace({ name: 'track' })
  },
  { immediate: true },
)
</script>

<template>
  <AppScreen :title="`Route ${routeCode}`" leading="back">
    <div class="relative h-full">
      <MapView :mode="mode" :basemap="basemap" :user-location="userLocation" />

      <MapControls
        :basemap="basemap"
        :is-locating="isLocating"
        :error="locationError"
        @toggle-basemap="toggleBasemap"
        @locate="locate"
      />

      <div class="pointer-events-none absolute inset-x-0 top-0 z-10 p-3">
        <div class="pointer-events-auto bg-background px-3 py-2 ring-1 ring-foreground/25">
          <p v-if="busRoute" class="truncate text-sm font-bold">{{ busRoute.name }}</p>
          <p class="mt-0.5 text-xs text-muted-foreground">{{ status }}</p>
          <!-- Names the stop the user came from, so the back button's destination
               is obvious rather than something they have to remember. -->
          <p v-if="originStop" class="mt-0.5 truncate text-xs text-muted-foreground">
            From {{ originStop.name }}
          </p>
        </div>
      </div>
    </div>
  </AppScreen>
</template>
